import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Plus, User, Phone, AlertTriangle, Edit2, Trash2, Camera, Lock } from 'lucide-react';
import { useAuth, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Form component defined OUTSIDE main component to prevent re-render issues
const RiderForm = ({ data, onChange, onSubmit, isEdit = false, onPhotoUpload, uploadingPhoto }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label>Name *</Label>
      <Input
        value={data.name || ''}
        onChange={(e) => onChange({ ...data, name: e.target.value })}
        placeholder="Full name"
        required
        data-testid="rider-name-input"
      />
    </div>

    {isEdit && data.rider_id && (
      <div className="space-y-2">
        <Label>Photo</Label>
        <div className="flex items-center gap-4">
          {data.photo_url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${data.photo_url}`}
              alt={data.name}
              className="w-20 h-20 rounded-full object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
              <User className="w-8 h-8 text-muted-foreground" />
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = (e) => onPhotoUpload(data.rider_id, e.target.files?.[0]);
              input.click();
            }}
            disabled={uploadingPhoto}
          >
            <Camera className="w-4 h-4 mr-2" />
            {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
          </Button>
        </div>
      </div>
    )}

    <div className="space-y-2">
      <Label>Date of Birth</Label>
      <Input
        type="date"
        value={data.birth_date || ''}
        onChange={(e) => onChange({ ...data, birth_date: e.target.value })}
        data-testid="rider-dob-input"
      />
    </div>

    <div className="space-y-2">
      <Label>Contact Number</Label>
      <Input
        value={data.contact_number || ''}
        onChange={(e) => onChange({ ...data, contact_number: e.target.value })}
        placeholder="Phone number"
        data-testid="rider-phone-input"
      />
    </div>

    <div className="border-t pt-4 mt-4">
      <h4 className="font-medium text-destructive flex items-center gap-2 mb-3">
        <AlertTriangle className="w-4 h-4" /> Emergency Contact
      </h4>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Emergency Contact Name</Label>
          <Input
            value={data.emergency_contact_name || ''}
            onChange={(e) => onChange({ ...data, emergency_contact_name: e.target.value })}
            placeholder="Name"
            data-testid="rider-emergency-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Emergency Contact Number</Label>
          <Input
            value={data.emergency_contact_number || ''}
            onChange={(e) => onChange({ ...data, emergency_contact_number: e.target.value })}
            placeholder="Phone number"
            data-testid="rider-emergency-phone-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Emergency Medical Notes</Label>
          <Textarea
            value={data.emergency_medical_notes || ''}
            onChange={(e) => onChange({ ...data, emergency_medical_notes: e.target.value })}
            placeholder="Allergies, medical conditions, medications..."
            data-testid="rider-medical-notes-input"
          />
        </div>
      </div>
    </div>

    <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-rider-btn">
      {isEdit ? 'Update Profile' : 'Create Profile'}
    </Button>
  </form>
);

export const RiderProfiles = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingRider, setViewingRider] = useState(null);
  const [editingRider, setEditingRider] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const emptyRider = {
    name: '',
    birth_date: '',
    contact_number: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    emergency_medical_notes: ''
  };

  const [newRider, setNewRider] = useState(emptyRider);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchRiders();
  }, [token]);

  const fetchRiders = async () => {
    try {
      const response = await fetch(`${API}/rider-profiles`, { headers: getHeaders() });
      if (response.ok) {
        setRiders(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch riders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRider = async (e) => {
    e.preventDefault();
    if (!newRider.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const response = await fetch(`${API}/rider-profiles`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(newRider)
      });

      if (response.ok) {
        const rider = await response.json();
        setRiders([...riders, rider]);
        setDialogOpen(false);
        setNewRider(emptyRider);
        toast.success('Rider profile created!');
      }
    } catch (error) {
      toast.error('Failed to create rider profile');
    }
  };

  const handleEditRider = async (e) => {
    e.preventDefault();
    if (!editingRider) return;

    try {
      const response = await fetch(`${API}/rider-profiles/${editingRider.rider_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          name: editingRider.name,
          birth_date: editingRider.birth_date,
          contact_number: editingRider.contact_number,
          emergency_contact_name: editingRider.emergency_contact_name,
          emergency_contact_number: editingRider.emergency_contact_number,
          emergency_medical_notes: editingRider.emergency_medical_notes
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setRiders(riders.map(r => r.rider_id === updated.rider_id ? updated : r));
        setEditDialogOpen(false);
        setEditingRider(null);
        toast.success('Rider profile updated!');
      }
    } catch (error) {
      toast.error('Failed to update rider profile');
    }
  };

  const handleDeleteRider = async (riderId) => {
    try {
      const response = await fetch(`${API}/rider-profiles/${riderId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setRiders(riders.filter(r => r.rider_id !== riderId));
        toast.success('Rider profile deleted');
      }
    } catch (error) {
      toast.error('Failed to delete rider profile');
    }
  };

  const handlePhotoUpload = async (riderId, file) => {
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
      const uploadHeaders = {};
      if (token) uploadHeaders['Authorization'] = `Bearer ${token}`;
      if (activeAccountId) uploadHeaders['X-Active-Account'] = activeAccountId;

      const response = await fetch(`${API}/upload/rider-photo/${riderId}`, {
        method: 'POST',
        headers: uploadHeaders,
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setRiders(riders.map(r => r.rider_id === riderId ? { ...r, photo_url: data.file_url } : r));
        if (editingRider && editingRider.rider_id === riderId) {
          setEditingRider({ ...editingRider, photo_url: data.file_url });
        }
        toast.success('Photo uploaded!');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 p-4 md:p-8 max-w-6xl mx-auto">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Rider Profiles</h1>
            <p className="text-muted-foreground mt-1">
              {riders.length} {riders.length === 1 ? 'rider' : 'riders'}
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                data-testid="add-rider-btn"
                onClick={(e) => {
                  if (!canAddEdit()) {
                    e.preventDefault();
                    requireSubscription();
                  }
                }}
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" /> Add Rider
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">New Rider Profile</DialogTitle>
              </DialogHeader>
              <RiderForm 
                data={newRider} 
                onChange={setNewRider} 
                onSubmit={handleAddRider}
                onPhotoUpload={handlePhotoUpload}
                uploadingPhoto={uploadingPhoto}
              />
            </DialogContent>
          </Dialog>
        </div>

        {riders.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Rider Profiles Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Create rider profiles to store contact information and emergency details for riders at your stable.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {riders.map((rider, i) => (
              <Card
                key={rider.rider_id}
                className="border-0 shadow-card bg-white animate-slideUp overflow-hidden card-hover cursor-pointer"
                style={{ animationDelay: `${i * 0.05}s` }}
                onClick={() => { setViewingRider(rider); setViewDialogOpen(true); }}
                data-testid={`rider-card-${rider.rider_id}`}
              >
                <div className="aspect-square relative bg-muted max-h-24">
                  {rider.photo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${rider.photo_url}`}
                      alt={rider.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-forest/10 to-saddle/10">
                      <User className="w-8 h-8 text-forest/30" />
                    </div>
                  )}
                </div>
                <CardContent className="p-3">
                  <h3 className="font-semibold text-sm truncate">{rider.name}</h3>
                  {rider.contact_number && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                      <Phone className="w-3 h-3 flex-shrink-0" /> {rider.contact_number}
                    </p>
                  )}
                  {rider.emergency_contact_name && (
                    <p className="text-xs text-destructive/70 flex items-center gap-1 mt-1 truncate">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {rider.emergency_contact_name}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingRider(rider);
                        setEditDialogOpen(true);
                      }}
                      data-testid={`edit-rider-${rider.rider_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" data-testid={`delete-rider-${rider.rider_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Rider Profile</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete {rider.name}'s profile? This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteRider(rider.rider_id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={(open) => { setViewDialogOpen(open); if (!open) setViewingRider(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">{viewingRider?.name}</DialogTitle>
            </DialogHeader>
            {viewingRider && (
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-4">
                  {viewingRider.photo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${viewingRider.photo_url}`}
                      alt={viewingRider.name}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-forest/10 flex items-center justify-center">
                      <User className="w-10 h-10 text-forest/30" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold">{viewingRider.name}</h3>
                    {viewingRider.birth_date && (
                      <p className="text-sm text-muted-foreground">
                        DOB: {format(parseISO(viewingRider.birth_date), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
                
                {viewingRider.contact_number && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{viewingRider.contact_number}</span>
                  </div>
                )}
                
                {viewingRider.emergency_contact_name && (
                  <div className="p-3 bg-destructive/5 rounded-lg border border-destructive/10">
                    <p className="text-xs font-medium text-destructive flex items-center gap-1 mb-2">
                      <AlertTriangle className="w-3 h-3" /> Emergency Contact
                    </p>
                    <p className="font-medium">{viewingRider.emergency_contact_name}</p>
                    {viewingRider.emergency_contact_number && (
                      <p className="text-sm text-muted-foreground">{viewingRider.emergency_contact_number}</p>
                    )}
                  </div>
                )}
                
                {viewingRider.emergency_medical_notes && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Medical Notes</p>
                    <p className="text-sm">{viewingRider.emergency_medical_notes}</p>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setViewDialogOpen(false); setEditingRider(viewingRider); setEditDialogOpen(true); }}
                  >
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-destructive"
                    onClick={() => { handleDeleteRider(viewingRider.rider_id); setViewDialogOpen(false); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setEditingRider(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Edit Rider Profile</DialogTitle>
            </DialogHeader>
            {editingRider && (
              <RiderForm 
                data={editingRider} 
                onChange={setEditingRider} 
                onSubmit={handleEditRider} 
                isEdit={true}
                onPhotoUpload={handlePhotoUpload}
                uploadingPhoto={uploadingPhoto}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
